// ── Early Bird 项目 wiki 式档案报告侧边抽屉 ────────────────────────────
// 替代六维评分详情页：早期/pre-token 项目没有链上数据，套六维评分模板大半维度
// 显示"数据缺失"，改用创始人/产品/公司/运营数据/媒体报道/竞品/参考来源七段式档案，
// 一整篇连续滚动展示（左侧导航是锚点跳转，不是切换 tab）——跟进笔记是可填写的
// 表单而非叙事内容，单独放一个不参与连续滚动的独立面板。
// 数据来源见 src/services/earlyBirdWiki.js（结构化字段直拼 + 搜索/官网/RootData/
// Surf 新闻 + AI 综合，引用编号校验防止编造来源）。
import { useState, useEffect, useRef } from 'react';
import { X, User, Package, Building2, Activity, Newspaper, Swords, BookOpen, RotateCw, Lock, Star, UserCheck, MessageSquare, Download, Link2, Maximize2, Minimize2 } from 'lucide-react';
import { useLang, useT } from '../i18n';
import { getOrStartEarlyBirdWiki, unsubscribeFromEarlyBirdWiki } from '../services/earlyBirdWiki';
import { WikiFoundersMention } from './EarlyBirdWikiFoundersMention';

const C = {
  surface: '#ffffff', border: '#e4e4e7', text: '#18181b',
  muted: '#71717a', faint: '#f4f4f5',
};

const SECTIONS = [
  { key: 'founders', label: '创始人', label_en: 'Founders', Icon: User },
  { key: 'product', label: '产品', label_en: 'Product', Icon: Package },
  { key: 'company', label: '公司', label_en: 'Company', Icon: Building2 },
  { key: 'operatingData', label: '运营数据', label_en: 'Operating Data', Icon: Activity },
  { key: 'media', label: '媒体报道', label_en: 'Media', Icon: Newspaper },
  { key: 'competitors', label: '竞品', label_en: 'Competitors', Icon: Swords },
  { key: 'sources', label: '参考来源', label_en: 'Sources', Icon: BookOpen },
];
const NOTES_SECTION = { key: 'notes', label: '跟进笔记', label_en: 'Follow-up notes', Icon: MessageSquare };

// 跟进状态：刻意保持粗粒度（渠道/回应/决策阶段揉在一个标签里），先把"记一笔+导出"
// 用起来看实际使用情况，等真正用出问题再考虑拆细——拆太细会导致每次记笔记要填的
// 字段变多，容易因为嫌麻烦而不填。
const NOTE_TYPES = [
  { value: '联系中', value_en: 'Reaching out', bg: '#e6f1fb', text: '#0c447c' },
  { value: '已回复', value_en: 'Replied', bg: '#eaf3de', text: '#27500a' },
  { value: '已见面', value_en: 'Met', bg: '#eeedfe', text: '#3c3489' },
  { value: '无回应', value_en: 'No response', bg: '#faeeda', text: '#633806' },
  { value: '放弃跟进', value_en: 'Dropped', bg: '#f1efe8', text: '#444441' },
];

function noteTypeMeta(type) {
  return NOTE_TYPES.find(t => t.value === type) || NOTE_TYPES[0];
}

function exportNotesAsMarkdown(projectName, notes) {
  const lines = [`# ${projectName} 跟进笔记`, ''];
  for (const n of notes) {
    const date = n.createdAt ? new Date(n.createdAt).toLocaleDateString('zh-CN') : '';
    lines.push(`- **${date}** [${n.type}] ${n.text}`);
  }
  const blob = new Blob([lines.join('\n')], { type: 'text/markdown' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${projectName}-跟进笔记.md`;
  a.click();
  URL.revokeObjectURL(url);
}

function InfoRow({ label, value, color, last }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: last ? 0 : 8 }}>
      <span style={{ color: C.muted }}>{label}</span>
      <span style={{ fontWeight: 600, color: color || C.text, textAlign: 'right', marginLeft: 8 }}>{value}</span>
    </div>
  );
}

export function EarlyBirdWikiModal({
  project, onClose, lang: langProp,
  user, isAdmin, creditsLeft, contactState, onUnlockContact, onOpenTipModal,
  isFollowing, onToggleFollow,
  notes = [], onAddNote,
}) {
  const { lang: langCtx } = useLang();
  const lang = langProp || langCtx;
  const t = useT();
  const [activeSection, setActiveSection] = useState('founders');
  const [showNotes, setShowNotes] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [wiki, setWiki] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [progress, setProgress] = useState({ stage: '', pct: 0 });
  const [noteType, setNoteType] = useState(NOTE_TYPES[0].value);
  const [noteText, setNoteText] = useState('');
  const [linkCopied, setLinkCopied] = useState(false);
  const [pendingScroll, setPendingScroll] = useState(null);
  const sectionRefs = useRef({});
  const scrollPaneRef = useRef(null);

  const copyShareLink = () => {
    const url = `${window.location.origin}/earlybird/${encodeURIComponent(project.id)}`;
    navigator.clipboard.writeText(url).then(() => {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 1500);
    }).catch(() => {});
  };

  // 生成过程登记在 earlyBirdWiki.js 的模块级 in-flight 表里，不挂在这个组件的生命
  // 周期上——关闭面板/切到别的项目不会让还没跑完的生成半途而废，重新打开同一个
  // 项目会直接接上进度，不会重新触发一次全新的（付费）生成。subscriptionRef 记录
  // "这个组件实例当前订阅的是哪个项目的哪一对回调"，方便下次调用前/卸载时精确摘掉，
  // 不会因为切换项目而继续收到上一个项目的进度更新。
  const subscriptionRef = useRef(null);

  const load = (forceRefresh = false) => {
    if (subscriptionRef.current) {
      unsubscribeFromEarlyBirdWiki(subscriptionRef.current.projectId, subscriptionRef.current.onProgress, subscriptionRef.current.onPartial);
      subscriptionRef.current = null;
    }
    (forceRefresh ? setRefreshing : setLoading)(true);
    setProgress({ stage: '', pct: 0 });
    setWiki(null);
    const onProgress = (stage, pct) => setProgress({ stage, pct });
    const onPartial = (partial) => setWiki({ ...partial, _partial: true });
    subscriptionRef.current = { projectId: project.id, onProgress, onPartial };
    getOrStartEarlyBirdWiki(project, forceRefresh, onProgress, onPartial)
      .then(setWiki)
      .catch(() => setWiki(null))
      .finally(() => { setLoading(false); setRefreshing(false); });
  };

  useEffect(() => {
    load(false);
    return () => {
      if (subscriptionRef.current) {
        unsubscribeFromEarlyBirdWiki(subscriptionRef.current.projectId, subscriptionRef.current.onProgress, subscriptionRef.current.onPartial);
        subscriptionRef.current = null;
      }
    };
  }, [project.id]); // eslint-disable-line

  // 点击左侧锚点导航：切回连续文档视图（如果当前在跟进笔记面板），
  // 再等文档重新挂载后滚动过去——不能在同一次事件里直接 scrollIntoView，
  // 因为跟进笔记面板卸载/文档面板挂载还没发生，ref 还没绑定上。
  useEffect(() => {
    if (!pendingScroll) return;
    const el = sectionRefs.current[pendingScroll];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setPendingScroll(null);
  }, [pendingScroll, showNotes]);

  const goToSection = (key) => {
    setActiveSection(key);
    setShowNotes(false);
    setPendingScroll(key);
  };

  // 滚动时高亮左侧导航里最接近顶部的段落，纯视觉反馈，不影响内容渲染
  const handleScroll = () => {
    const pane = scrollPaneRef.current;
    if (!pane || showNotes) return;
    const paneTop = pane.getBoundingClientRect().top;
    let closest = null;
    let closestDist = Infinity;
    for (const s of SECTIONS) {
      const el = sectionRefs.current[s.key];
      if (!el) continue;
      const dist = el.getBoundingClientRect().top - paneTop;
      if (dist <= 40 && Math.abs(dist) < closestDist) { closestDist = Math.abs(dist); closest = s.key; }
    }
    if (closest && closest !== activeSection) setActiveSection(closest);
  };

  // 文本里的 [n] 引用标号渲染成小号上标链接，点击跳到参考来源段落
  const renderTextWithCitations = (text) => {
    if (!text) return null;
    return String(text).split(/(\[\d+\])/g).map((part, i) => {
      if (!/^\[\d+\]$/.test(part)) return <span key={i}>{part}</span>;
      return (
        <a key={i} href="#" onClick={(e) => { e.preventDefault(); goToSection('sources'); }} style={{
          color: '#2563eb', textDecoration: 'none', fontSize: 10, verticalAlign: 'super', marginLeft: 1,
        }}>{part}</a>
      );
    });
  };

  // pending=true：产品/运营数据已经先展示出来了，但这段本身还没跑完（还在搜索/AI 综合），
  // 跟"跑完了但确实没搜到"要分开说，不然用户会以为已经搜过了
  const renderBody = (text, pending) => (
    <div style={{ fontSize: 13, lineHeight: 1.8, color: pending ? C.muted : C.text, whiteSpace: 'pre-wrap', fontStyle: pending ? 'italic' : 'normal' }}>
      {text ? renderTextWithCitations(text)
        : pending ? (lang === 'en' ? 'Generating…' : '生成中…')
        : (lang === 'en' ? 'No information found.' : '未搜索到公开信息。')}
    </div>
  );

  const renderSection = (key, content) => {
    const meta = SECTIONS.find(s => s.key === key);
    const idx = SECTIONS.findIndex(s => s.key === key);
    return (
      <div key={key} ref={(el) => { sectionRefs.current[key] = el; }} style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 10 }}>
          {idx + 1}. {lang === 'en' ? meta.label_en : meta.label}
        </div>
        {content}
      </div>
    );
  };

  const founderKnown = wiki?.founders && !/未搜索到|no information/i.test(wiki.founders);

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 2000,
      display: 'flex', justifyContent: 'flex-end',
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: C.surface, width: fullscreen ? '100%' : 760, maxWidth: '100%', height: '100%',
        display: 'flex', flexDirection: 'column', overflow: 'hidden', borderLeft: `1px solid ${C.border}`,
      }}>
        {/* 顶部条 */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {lang === 'en' ? 'Project Wiki' : 'Project Wiki'}
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button onClick={copyShareLink} title={lang === 'en' ? 'Copy share link' : '复制分享链接'} style={{
              background: 'none', border: 'none', cursor: 'pointer', color: linkCopied ? '#16a34a' : C.muted,
              display: 'flex', alignItems: 'center', gap: 4, fontSize: 11,
            }}>
              <Link2 size={12} strokeWidth={2} />
              {linkCopied ? (lang === 'en' ? 'Copied' : '已复制') : (lang === 'en' ? 'Copy link' : '复制链接')}
            </button>
            <button onClick={() => load(true)} disabled={refreshing} title={lang === 'en' ? 'Regenerate' : '重新生成'} style={{
              background: 'none', border: 'none', cursor: refreshing ? 'default' : 'pointer', color: C.muted,
              display: 'flex', alignItems: 'center', gap: 4, fontSize: 11,
            }}>
              <RotateCw size={12} strokeWidth={2} style={refreshing ? { animation: 'spin 1s linear infinite' } : undefined} />
              {refreshing ? (lang === 'en' ? 'Regenerating…' : '重新生成中…') : (lang === 'en' ? 'Regenerate' : '重新生成')}
            </button>
            <button onClick={() => setFullscreen(f => !f)} title={fullscreen ? (lang === 'en' ? 'Exit fullscreen' : '退出全屏') : (lang === 'en' ? 'Fullscreen' : '全屏阅读')} style={{
              background: 'none', border: 'none', cursor: 'pointer', color: C.muted,
              display: 'flex', alignItems: 'center', gap: 4, fontSize: 11,
            }}>
              {fullscreen ? <Minimize2 size={12} strokeWidth={2} /> : <Maximize2 size={12} strokeWidth={2} />}
              {fullscreen ? (lang === 'en' ? 'Exit fullscreen' : '退出全屏') : (lang === 'en' ? 'Fullscreen' : '全屏阅读')}
            </button>
            <button onClick={onClose} aria-label="close" style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted, display: 'flex' }}>
              <X size={16} strokeWidth={2} />
            </button>
          </div>
        </div>

        {(loading || refreshing) && (
          <div style={{ padding: '8px 16px', borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
              <span style={{ fontSize: 11, color: C.muted }}>{progress.stage || (lang === 'en' ? 'Researching…' : '正在生成档案…')}</span>
              <span style={{ fontSize: 11, color: C.muted, fontWeight: 600 }}>{progress.pct}%</span>
            </div>
            <div style={{ height: 4, borderRadius: 999, background: C.faint, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${progress.pct}%`, background: C.text, borderRadius: 999, transition: 'width .4s ease' }} />
            </div>
          </div>
        )}

        <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
          {/* 左侧锚点导航 */}
          <div style={{ width: 150, flexShrink: 0, borderRight: `1px solid ${C.border}`, padding: '10px 8px', overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
            <div style={{ flex: 1 }}>
              {SECTIONS.map((s, i) => (
                <button key={s.key} onClick={() => goToSection(s.key)} style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: 7, textAlign: 'left',
                  padding: '8px 10px', borderRadius: 6, border: 'none', cursor: 'pointer', marginBottom: 2,
                  background: !showNotes && activeSection === s.key ? C.text : 'transparent',
                  color: !showNotes && activeSection === s.key ? '#fff' : C.text,
                  fontSize: 12, fontWeight: !showNotes && activeSection === s.key ? 600 : 400,
                }}>
                  <s.Icon size={13} strokeWidth={2} style={{ flexShrink: 0 }} />
                  <span style={{ flex: 1 }}>{i + 1}. {lang === 'en' ? s.label_en : s.label}</span>
                </button>
              ))}
            </div>
            <div style={{ borderTop: `1px solid ${C.faint}`, marginTop: 6, paddingTop: 6 }}>
              <button onClick={() => setShowNotes(true)} style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 7, textAlign: 'left',
                padding: '8px 10px', borderRadius: 6, border: 'none', cursor: 'pointer',
                background: showNotes ? C.text : 'transparent',
                color: showNotes ? '#fff' : C.text, fontSize: 12, fontWeight: showNotes ? 600 : 400,
              }}>
                <NOTES_SECTION.Icon size={13} strokeWidth={2} style={{ flexShrink: 0 }} />
                <span style={{ flex: 1 }}>{lang === 'en' ? NOTES_SECTION.label_en : NOTES_SECTION.label}</span>
              </button>
            </div>
          </div>

          {/* 右侧内容：跟进笔记是独立表单面板；其余七段连续滚动展示成一整篇文档 */}
          <div ref={scrollPaneRef} onScroll={handleScroll} style={{ flex: 1, padding: 20, overflowY: 'auto' }}>
            <div style={{ display: 'flex', gap: 16 }}>
              <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 12 }}>
                {wiki?.twitterProfile?.avatar && (
                  <img src={wiki.twitterProfile.avatar} alt="" width={40} height={40} style={{ borderRadius: '50%', flexShrink: 0, objectFit: 'cover' }} />
                )}
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 20, fontWeight: 700 }}>{project.project_name}</div>
                  <div style={{ fontSize: 11, color: C.muted, marginTop: 4, fontStyle: 'italic' }}>
                    {project.organization}{project.program ? ` · ${project.program}` : ''}
                  </div>
                </div>
              </div>
              <div style={{ width: 170, flexShrink: 0, background: C.faint, border: `1px solid ${C.border}`, borderRadius: 8, padding: '10px 12px', fontSize: 11, alignSelf: 'flex-start' }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
                  {lang === 'en' ? 'Project profile' : '项目档案'}
                </div>
                <InfoRow label={lang === 'en' ? 'Track' : '赛道'} value={project.tax_category || '—'} />
                <InfoRow label={lang === 'en' ? 'Founder' : '创始人'} value={wiki ? (wiki._partial ? (lang === 'en' ? 'Generating…' : '生成中…') : founderKnown ? (lang === 'en' ? 'Found' : '已找到') : (lang === 'en' ? 'Unknown' : '未搜索到')) : '—'} />
                <InfoRow label={lang === 'en' ? 'Stage' : '阶段'} value={lang === 'en' ? 'Pre-funding' : '融资未明'} />
                {wiki?.twitterProfile?.followers != null ? (
                  <InfoRow label={lang === 'en' ? 'Followers' : '粉丝数'} value={wiki.twitterProfile.followers.toLocaleString(lang === 'en' ? 'en-US' : 'zh-CN')} />
                ) : null}
                <InfoRow label={lang === 'en' ? 'Status' : '状态'} value="active" color="#16a34a" last />
              </div>
            </div>
            <div style={{ height: 1, background: C.faint, margin: '12px 0 18px' }} />

            {showNotes ? (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <div style={{ fontSize: 15, fontWeight: 700 }}>{lang === 'en' ? 'Follow-up notes' : '跟进笔记'}</div>
                  {notes.length > 0 && (
                    <button onClick={() => exportNotesAsMarkdown(project.project_name, notes)} style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4, background: 'none',
                      border: `1px solid ${C.border}`, borderRadius: 999, padding: '4px 10px',
                      fontSize: 11, color: C.muted, cursor: 'pointer',
                    }}>
                      <Download size={11} strokeWidth={2} />
                      {lang === 'en' ? 'Export as Markdown' : '导出为 Markdown'}
                    </button>
                  )}
                </div>

                {onAddNote && (
                  <div style={{ background: C.faint, borderRadius: 8, padding: 10, marginBottom: 14 }}>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                      <select value={noteType} onChange={(e) => setNoteType(e.target.value)} style={{
                        flexShrink: 0, width: 100, fontSize: 12, borderRadius: 6, border: `1px solid ${C.border}`, padding: '4px 6px',
                      }}>
                        {NOTE_TYPES.map(t => (
                          <option key={t.value} value={t.value}>{lang === 'en' ? t.value_en : t.value}</option>
                        ))}
                      </select>
                      <input
                        value={noteText}
                        onChange={(e) => setNoteText(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key !== 'Enter' || !noteText.trim()) return;
                          onAddNote({ type: noteType, text: noteText.trim() });
                          setNoteText('');
                        }}
                        placeholder={lang === 'en' ? 'e.g. Founder replied on Twitter DM, open to a call' : '记一笔，比如：创始人回复了 Twitter DM，愿意约个电话'}
                        style={{ flex: 1, fontSize: 12, borderRadius: 6, border: `1px solid ${C.border}`, padding: '4px 8px' }}
                      />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <button onClick={() => {
                        if (!noteText.trim()) return;
                        onAddNote({ type: noteType, text: noteText.trim() });
                        setNoteText('');
                      }} style={{
                        fontSize: 11, padding: '4px 12px', borderRadius: 999, border: `1px solid ${C.text}`,
                        background: C.text, color: '#fff', cursor: 'pointer',
                      }}>{lang === 'en' ? 'Add' : '添加记录'}</button>
                    </div>
                  </div>
                )}

                {notes.length === 0 ? (
                  <div style={{ color: C.muted, fontSize: 12 }}>{lang === 'en' ? 'No notes yet.' : '还没有跟进记录。'}</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {[...notes].reverse().map((n) => {
                      const meta = noteTypeMeta(n.type);
                      return (
                        <div key={n.id || n.createdAt} style={{ display: 'flex', gap: 10, paddingBottom: 10, borderBottom: `1px solid ${C.faint}` }}>
                          <div style={{ fontSize: 11, color: C.muted, width: 68, flexShrink: 0, paddingTop: 2 }}>
                            {n.createdAt ? new Date(n.createdAt).toLocaleDateString('zh-CN', { month: '2-digit', day: '2-digit' }) : ''}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <span style={{ background: meta.bg, color: meta.text, fontSize: 10, padding: '2px 8px', borderRadius: 999, display: 'inline-block', marginBottom: 4 }}>
                              {lang === 'en' ? meta.value_en : meta.value}
                            </span>
                            <div style={{ fontSize: 12.5, lineHeight: 1.5 }}>{n.text}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : loading && !wiki ? null : !wiki ? (
              <div style={{ color: C.muted, fontSize: 12, padding: '20px 0' }}>{lang === 'en' ? 'Failed to generate. Try again.' : '生成失败，请点"重新生成"重试。'}</div>
            ) : (
              <div>
                {renderSection('founders', <>
                  {renderBody(lang === 'en' ? (wiki.founders_en || wiki.founders) : wiki.founders, wiki._partial)}
                  {lang === 'en' && wiki.founders && !wiki.founders_en && t('misc.ai_content_note') && (
                    <div style={{ color: C.muted, fontSize: 11, marginTop: 4 }}>{t('misc.ai_content_note')}</div>
                  )}
                </>)}
                {renderSection('product', renderBody(lang === 'en' ? (wiki.productEn || wiki.product) : wiki.product))}
                {renderSection('company', <>
                  {renderBody(lang === 'en' ? (wiki.company_en || wiki.company) : wiki.company, wiki._partial)}
                  {lang === 'en' && wiki.company && !wiki.company_en && t('misc.ai_content_note') && (
                    <div style={{ color: C.muted, fontSize: 11, marginTop: 4 }}>{t('misc.ai_content_note')}</div>
                  )}
                </>)}
                {renderSection('operatingData', renderBody(lang === 'en' ? (wiki.operatingDataEn || wiki.operatingData) : wiki.operatingData))}
                {renderSection('media', wiki.media?.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {wiki.media.map((n, i) => (
                      <a key={i} href={n.url} target="_blank" rel="noreferrer" style={{
                        display: 'block', border: `1px solid ${C.border}`, borderRadius: 6, padding: '8px 10px',
                        textDecoration: 'none', color: 'inherit',
                      }}>
                        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 2 }}>{n.title}</div>
                        <div style={{ fontSize: 11, color: C.muted }}>
                          {n.source || 'News'}{n.published_at ? ' · ' + new Date(n.published_at * 1000).toLocaleDateString('zh-CN') : ''}
                        </div>
                      </a>
                    ))}
                    <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
                      {lang === 'en' ? 'Data via Surf news feed, sorted by recency.' : '数据来自 Surf 新闻接口，按时间排序。'}
                    </div>
                  </div>
                ) : (
                  <div style={{ color: C.muted, fontSize: 12, fontStyle: wiki._partial ? 'italic' : 'normal' }}>
                    {wiki._partial ? (lang === 'en' ? 'Generating…' : '生成中…') : (lang === 'en' ? 'No related news found.' : '暂无相关新闻报道。')}
                  </div>
                ))}
                {renderSection('competitors', <>
                  {renderBody(lang === 'en' ? (wiki.competitors_en || wiki.competitors) : wiki.competitors, wiki._partial)}
                  {lang === 'en' && wiki.competitors && !wiki.competitors_en && t('misc.ai_content_note') && (
                    <div style={{ color: C.muted, fontSize: 11, marginTop: 4 }}>{t('misc.ai_content_note')}</div>
                  )}
                </>)}
                {renderSection('sources', wiki.sources?.length > 0 ? (
                  <ol style={{ margin: 0, paddingLeft: 18, fontSize: 12, lineHeight: 1.9 }}>
                    {wiki.sources.map((s) => (
                      <li key={s.index}>
                        <a href={s.url} target="_blank" rel="noreferrer" style={{ color: '#2563eb' }}>{s.title || s.url}</a>
                      </li>
                    ))}
                  </ol>
                ) : (
                  <div style={{ color: C.muted, fontSize: 12, fontStyle: wiki._partial ? 'italic' : 'normal' }}>
                    {wiki._partial ? (lang === 'en' ? 'Generating…' : '生成中…') : (lang === 'en' ? 'No search results found for this project.' : '未搜索到相关公开信息。')}
                  </div>
                ))}
              </div>
            )}

            {/* 联系方式 / 关注 / 我认识 */}
            {onUnlockContact && (
              <div style={{ marginTop: 20, paddingTop: 14, borderTop: `1px solid ${C.faint}`, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 11, color: C.muted, display: 'inline-flex', alignItems: 'center', gap: 4, marginRight: 'auto' }}>
                  <Lock size={11} strokeWidth={2} />
                  {user
                    ? (isAdmin
                        ? (lang === 'en' ? 'Contact · unlimited (admin)' : '联系方式 · 管理员不限次数')
                        : (lang === 'en' ? `Contact · ${creditsLeft} left` : `联系方式 · 剩 ${creditsLeft} 次`))
                    : (lang === 'en' ? 'Sign in for contact' : '登录后可获取联系方式')}
                </span>
                <button onClick={() => onUnlockContact(project)} style={{
                  padding: '6px 14px', background: C.text, border: `1px solid ${C.text}`, color: '#fff',
                  borderRadius: 999, fontSize: 11, fontWeight: 500, cursor: 'pointer',
                }}>
                  {contactState?.status === 'loading' ? (lang === 'en' ? 'Fetching…' : '获取中…') : (lang === 'en' ? 'Get contact' : '求联系方式')}
                </button>
                <button onClick={() => onOpenTipModal(project)} style={{
                  padding: '6px 14px', background: C.surface, border: `1px solid ${C.border}`, borderRadius: 999,
                  fontSize: 11, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4,
                }}><UserCheck size={11} strokeWidth={2} />{lang === 'en' ? 'I know them' : '我认识'}</button>
                <button onClick={() => onToggleFollow(project.id)} style={{
                  padding: '6px 14px', background: C.surface, border: `1px solid ${C.border}`, borderRadius: 999,
                  fontSize: 11, cursor: 'pointer', color: isFollowing ? '#b45309' : C.text,
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                }}><Star size={11} strokeWidth={2} fill={isFollowing ? '#f59e0b' : 'none'} />{isFollowing ? (lang === 'en' ? 'Following' : '已关注') : (lang === 'en' ? 'Follow' : '关注')}</button>
                {contactState?.status === 'shown' && (
                  <div style={{ width: '100%', marginTop: 6, padding: '8px 10px', background: C.faint, borderRadius: 6, fontSize: 11.5 }}>
                    {contactState.contact.email && <div>Email：<a href={`mailto:${contactState.contact.email}`}>{contactState.contact.email}</a></div>}
                    {contactState.contact.twitterUsername && <div>Twitter：<a href={`https://x.com/${contactState.contact.twitterUsername}`} target="_blank" rel="noreferrer">@{contactState.contact.twitterUsername}</a></div>}
                    {contactState.contact.website && <div>{lang === 'en' ? 'Site' : '站点'}：<a href={/^https?:/.test(contactState.contact.website) ? contactState.contact.website : `https://${contactState.contact.website}`} target="_blank" rel="noreferrer">{contactState.contact.website}</a></div>}
                    {contactState.contact.team?.length > 0 && (
                      <div style={{ marginTop: 4 }}>
                        {lang === 'en' ? 'Team (LinkedIn)' : '团队（LinkedIn）'}：
                        {contactState.contact.team.map((m, i) => (
                          <span key={i}>
                            {i > 0 && '、'}
                            <a href={m.linkedin} target="_blank" rel="noreferrer">{m.name}{m.role ? `（${m.role}）` : ''}</a>
                          </span>
                        ))}
                      </div>
                    )}
                    {contactState.contact.colosseumTeam?.length > 0 && (
                      <div style={{ marginTop: 4 }}>
                        {lang === 'en' ? 'Team (Colosseum)' : '团队（Colosseum）'}：
                        {contactState.contact.colosseumTeam.map((t, i) => (
                          <span key={t.profileUrl}>
                            {i > 0 && '、'}
                            <a href={t.profileUrl} target="_blank" rel="noreferrer">{t.handle}</a>
                          </span>
                        ))}
                      </div>
                    )}
                    {contactState.wikiFounders && <WikiFoundersMention text={contactState.wikiFounders} lang={lang} />}
                  </div>
                )}
                {contactState?.status === 'none' && (
                  <div style={{ width: '100%', marginTop: 6, padding: '8px 10px', background: C.faint, borderRadius: 6, fontSize: 11.5 }}>
                    <div style={{ color: C.muted }}>
                      {lang === 'en' ? 'No self-disclosed contact found (credit not used)' : '未找到本人公开的联系方式（未扣额度）'}
                    </div>
                    {contactState.wikiFounders && <WikiFoundersMention text={contactState.wikiFounders} lang={lang} />}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
